import { GeneralCalculationObject } from '../general-calculation/index';

import defaultOptions from './defaultOptions';

import Arrow from '../../objects/arrow';
import { createArrow } from '../../services/helper';
import { HTMLEditableLabel } from '../../objects/html-label';
import { defaultUnits } from '@config';
import { unitTransform, unitLabel, mergeDeep } from '../../../services/helper.service';

import Scene from "@scene";

export default class BracketAttachmentObject extends GeneralCalculationObject {

  constructor(options = {}) {
    super(mergeDeep({}, options, { isBracket: true }) , defaultOptions);
  }

  setOrientation() {
    super.setOrientation();

    this.layer3D.mesh.rotation.x = Math.PI / 2;
    this.layer3D.mesh.rotation.y = 0;
    this.layer3D.mesh.rotation.z = 0;
  }

  update(options) {
    super.update(options);
  }

  init3DObject() {
    super.init3DObject();

    if (this.options.object.loads.display && this.beam.size.height) {
      let loadsArrowSize = 70;

      // Display L arrow
      const lLabel = createLoadsLabel({
        arrow: {
          size: this.beam.size.height,
          color: 0x000000,
          isSingle: false,
          direction: Arrow.types.direction.X,
          position: {
            x: this.panel.size.depth / 2,
            y: this.beam.position.x,
            z: this.beam.position.z - this.beam.size.width / 2
          }
        },
        label: {
          prefix: 'L =',
          value: unitTransform(+this.beam.size.height).from(defaultUnits.DISTANCE).to(Scene.units.distance),
          position: {
            x: this.beam.position.z - this.beam.size.width / 2,
            y: this.beam.size.height / 2,
            z: this.beam.position.x
          },
          align: HTMLEditableLabel.types.align.CENTER,
          verticalAlign: HTMLEditableLabel.types.verticalAlign.BOTTOM
        }
      });
      lLabel.label.onInputChange((val, didChange) => {
        this.dispatchOptionUpdate({
          beam: {
            height: unitTransform(+val, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
          }
        },
        false,
        didChange);
      });
      this.layer3D.addMesh(lLabel.arrow.mesh);
      this.layer3D.htmlLayer.add(lLabel.label);

      // Display Lg arrow
      const lgLabel = createLoadsLabel({
        arrow: {
          size: this.options.beam.loads.lg,
          color: 0x000000,
          isSingle: false,
          direction: Arrow.types.direction.X,
          position: {
            x: 0,
            y: this.beam.position.x,
            z: this.beam.position.z
          }
        },
        label: {
          prefix: 'Lg =',
          value: unitTransform(+this.options.beam.loads.lg).from(defaultUnits.DISTANCE).to(Scene.units.distance),
          position: {
            x: this.beam.position.z,
            y: this.options.beam.loads.lg / 2,
            z: this.beam.position.x
          },
          align: HTMLEditableLabel.types.align.CENTER
        }
      });
      lgLabel.label.onInputChange((val, didChange) => {
        this.dispatchOptionUpdate({
          beam: {
            loads: { 
              lg: unitTransform(+val, false).from(Scene.units.distance).to(defaultUnits.DISTANCE)
            }
          }
        },
        false,
        didChange);
      });
      this.layer3D.addMesh(lgLabel.arrow.mesh);
      this.layer3D.htmlLayer.add(lgLabel.label);

      // Display Load P
      const pLabel = createLoadsLabel({
        arrow: {
          size: loadsArrowSize * 2,
          color: 0x0000ff,
          isSingle: true,
          direction: this.options.beam.loads.weightP < 0 ? Arrow.types.direction.Z : Arrow.types.direction.NegativeZ,
          position: {
            x: this.options.beam.loads.lg,
            y: this.beam.position.x,
            z: this.beam.position.z
          }
        },
        label: {
          prefix: 'P =',
          value: unitTransform(+this.options.beam.loads.weightP).from(defaultUnits.WEIGHT).to(Scene.units.weight),
          postfix: unitLabel(Scene.units.weight),
          position: {
            x: this.beam.position.z + (this.options.beam.loads.weightP < 0 ? -loadsArrowSize * 2 : loadsArrowSize * 2 + 15),
            y: this.options.beam.loads.lg,
            z: this.beam.position.x
          },
          align: HTMLEditableLabel.types.align.CENTER,
          verticalAlign: HTMLEditableLabel.types.align.CENTER
        }
      });
      pLabel.label.onInputChange((val, didChange) => {
        this.dispatchOptionUpdate({
          beam: {
            loads: { 
              weightP: unitTransform(+val, false).from(Scene.units.weight).to(defaultUnits.WEIGHT)
            }
          }
        },
        false,
        didChange);
      });
      this.layer3D.addMesh(pLabel.arrow.mesh);
      this.layer3D.htmlLayer.add(pLabel.label);

      // Display Load F
      const fLabel = createLoadsLabel({
        arrow: {
          size: loadsArrowSize * 2,
          color: 0x00ff00,
          isSingle: true,
          direction: this.options.beam.loads.loadF < 0 ? Arrow.types.direction.Z : Arrow.types.direction.NegativeZ,
          position: {
            x: this.beam.size.height,
            y: this.beam.position.x,
            z: this.beam.position.z
          }
        },
        label: {
          prefix: 'F =',
          value: unitTransform(+this.options.beam.loads.loadF).from(defaultUnits.LOAD).to(Scene.units.load),
          postfix: unitLabel(Scene.units.load),
          position: {
            x: this.beam.position.z + (this.options.beam.loads.loadF < 0 ? -loadsArrowSize * 2 : loadsArrowSize * 2 + 15),
            y: this.beam.size.height,
            z: this.beam.position.x
          },
          align: HTMLEditableLabel.types.align.CENTER,
          verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER
        }
      });
      fLabel.label.onInputChange((val, didChange) => {
        this.dispatchOptionUpdate({
          beam: {
            loads: { 
              loadF: unitTransform(+val, false).from(Scene.units.load).to(defaultUnits.LOAD)
            }
          }
        },
        false,
        didChange);
      });
      this.layer3D.addMesh(fLabel.arrow.mesh);
      this.layer3D.htmlLayer.add(fLabel.label);

      // Display Load NR
      const nrLabel = createLoadsLabel({
        arrow: {
          size: loadsArrowSize,
          isSingle: true,
          direction: this.options.beam.loads.loadNr < 0 ? Arrow.types.direction.NegativeX : Arrow.types.direction.X,
          position: {
            x: this.beam.size.height,
            y: this.beam.position.x,
            z: this.beam.position.z
          }
        },
        label: {
          prefix: 'NR =',
          value: unitTransform(+this.options.beam.loads.loadNr).from(defaultUnits.LOAD).to(Scene.units.load),
          postfix: unitLabel(Scene.units.load),
          position: {
            x: this.beam.position.z,
            y: (this.options.beam.loads.loadNr < 0 ? 
                this.beam.size.height - loadsArrowSize - 15 : 
                this.beam.size.height + loadsArrowSize + 15),
            z: this.beam.position.x
          },
          align: HTMLEditableLabel.types.align.CENTER,
          verticalAlign: HTMLEditableLabel.types.verticalAlign.CENTER
        }
      });
      nrLabel.label.onInputChange((val, didChange) => {
        this.dispatchOptionUpdate({
          beam: {
            loads: { 
              loadNr: unitTransform(+val, false).from(Scene.units.load).to(defaultUnits.LOAD)
            }
          }
        },
        false,
        didChange);
      });
      this.layer3D.addMesh(nrLabel.arrow.mesh);
      this.layer3D.htmlLayer.add(nrLabel.label);
    }
  }

  init2DObject() {
    super.init2DObject();
  }

}

function createLoadsLabel(options) {
  const arrow = createArrow(options.arrow.direction, options.arrow.isSingle, {
    size: options.arrow.size,
    color: options.arrow.color
  });
  if (options.arrow.position) {
    arrow.mesh.position.x += options.arrow.position.x || 0;
    arrow.mesh.position.y += options.arrow.position.y || 0;
    arrow.mesh.position.z += options.arrow.position.z || 0;
  }

  const labelObject = new HTMLEditableLabel({
    text: `${options.label.prefix} ${options.label.value} ${options.label.postfix || ''}`,
    value: options.label.value,
    align: options.label.align,
    verticalAlign: options.label.verticalAlign
  });
  
  if (options.label.position) {
    labelObject.position.set(
      options.label.position.x || 0,
      options.label.position.y || 0,
      options.label.position.z || 0,
    );
  }

  return {
    arrow,
    label: labelObject
  };
}



// WEBPACK FOOTER //
// ./src/object-view/generators/bracket-attachment/bracket-attachment.object.js